// src/utils/vofStatement.js
// ============================================================================
// Pure helpers for the VOF hours statement (Reports > VOF statement) and the
// Travel page. No Supabase, no React: the classification itself lives in the
// view public.vof_hours_statement, and this file only picks periods, groups
// the view's rows and writes CSV.
//
// Dates are 'YYYY-MM-DD' strings throughout and all arithmetic runs in UTC,
// so a browser in Rotterdam and one in Mumbai pick the same quarter.
// ============================================================================

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Contract quarters start on the 14th of Jan, Apr, Jul and Oct. The
// intercompany agreement starts with the quarter of 14 Jul 2026.
export const CONTRACT_QUARTER_DAY = 14;
export const CONTRACT_START = '2026-07-14';
// Calendar months and quarters are offered from the same year.
export const FIRST_YEAR = 2026;

export const GENERAL_PROJECT_CODES = ['BRG', 'BRA'];

const pad = (n) => String(n).padStart(2, '0');

export const isoDate = (y, m0, d) => {
  const dt = new Date(Date.UTC(y, m0, d));
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
};

const parts = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return { y, m0: m - 1, d };
};

export const formatDay = (iso) => {
  if (!iso) return '';
  const { y, m0, d } = parts(iso);
  return `${d} ${MONTHS[m0]} ${y}`;
};

export const todayIso = (now = new Date()) =>
  isoDate(now.getFullYear(), now.getMonth(), now.getDate());

/** The contract quarter that contains `iso`. */
export const contractQuarterFor = (iso) => {
  const { y, m0, d } = parts(iso);
  // Quarter start months are 0, 3, 6, 9. A date before the 14th belongs to
  // the quarter that started on the 14th of an earlier month.
  let startM0 = m0 - (m0 % 3);
  let startY = y;
  if (m0 === startM0 && d < CONTRACT_QUARTER_DAY) startM0 -= 3;
  if (startM0 < 0) { startM0 += 12; startY -= 1; }
  const from = isoDate(startY, startM0, CONTRACT_QUARTER_DAY);
  const to = isoDate(startY, startM0 + 3, CONTRACT_QUARTER_DAY - 1);
  return { from, to };
};

/** Contract quarters from 14 Jul 2026 up to the one containing `today`, newest first. */
export const contractQuarters = (today) => {
  const out = [];
  const last = contractQuarterFor(today < CONTRACT_START ? CONTRACT_START : today);
  let q = contractQuarterFor(CONTRACT_START);
  while (q.from <= last.from) {
    out.push({
      id: `cq:${q.from}`,
      kind: 'contract',
      label: `${formatDay(q.from)} to ${formatDay(q.to)}`,
      from: q.from,
      to: q.to,
    });
    const { y, m0 } = parts(q.from);
    q = contractQuarterFor(isoDate(y, m0 + 3, CONTRACT_QUARTER_DAY));
  }
  return out.reverse();
};

/** Calendar quarters from Q1 FIRST_YEAR to the one containing `today`, newest first. */
export const calendarQuarters = (today) => {
  const { y: ty, m0: tm } = parts(today);
  const out = [];
  for (let y = FIRST_YEAR; y <= ty; y += 1) {
    for (let q = 0; q < 4; q += 1) {
      if (y === ty && q * 3 > tm) break;
      out.push({
        id: `q:${y}-${q + 1}`,
        kind: 'calendar',
        label: `Q${q + 1} ${y} (${MONTHS[q * 3]} to ${MONTHS[q * 3 + 2]})`,
        from: isoDate(y, q * 3, 1),
        to: isoDate(y, q * 3 + 3, 0),
      });
    }
  }
  return out.reverse();
};

/** Calendar months from Jan FIRST_YEAR to the month of `today`, newest first. */
export const calendarMonths = (today) => {
  const { y: ty, m0: tm } = parts(today);
  const out = [];
  for (let y = FIRST_YEAR; y <= ty; y += 1) {
    for (let m = 0; m < 12; m += 1) {
      if (y === ty && m > tm) break;
      out.push({
        id: `m:${y}-${pad(m + 1)}`,
        kind: 'month',
        label: `${MONTHS[m]} ${y}`,
        from: isoDate(y, m, 1),
        to: isoDate(y, m + 1, 0),
      });
    }
  }
  return out.reverse();
};

// ── Travel log ──────────────────────────────────────────────────────────────

/**
 * Rows of the same person whose date ranges share at least one day with
 * `candidate`. Deleted rows and the candidate itself (same id) are skipped.
 */
export const findTravelOverlaps = (rows, candidate) => {
  if (!candidate?.userId || !candidate.startDate || !candidate.endDate) return [];
  return (rows || []).filter(r =>
    !r.deletedAt
    && String(r.userId) === String(candidate.userId)
    && (candidate.id == null || String(r.id) !== String(candidate.id))
    && r.startDate <= candidate.endDate
    && r.endDate >= candidate.startDate,
  );
};

/** Inclusive day count of a travel row. */
export const travelDays = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  const a = Date.UTC(...Object.values(parts(startDate)));
  const b = Date.UTC(...Object.values(parts(endDate)));
  return Math.round((b - a) / 86400000) + 1;
};

// ── Statement ───────────────────────────────────────────────────────────────

export const MODULE_A = 'Module A';
export const MODULE_B = 'Module B';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * The heading an excluded partner row is counted under. India days are split
 * into project work and general / administration, since only the first is
 * work SBPL would otherwise have been billed for.
 */
export const MOVED_TO_SBPL = 'Moved to SBPL';

export const excludedBucket = (row) => {
  if (row.overrideId != null) return MOVED_TO_SBPL;
  if (row.reason === 'India day') {
    return GENERAL_PROJECT_CODES.includes(row.projectCode)
      ? 'India day, general or administration'
      : 'India day, project work';
  }
  return row.reason;
};

export const EXCLUDED_BUCKETS = [
  'India day, project work',
  'India day, general or administration',
  'general or director',
  'ABVOF project',
  MOVED_TO_SBPL,
];

/**
 * Maps a vof_hours_statement row (snake_case) to the shape the page uses.
 */
export const mapStatementRow = (r) => ({
  entryId: r.entry_id,
  date: r.date,
  userId: r.user_id,
  userName: r.user_name,
  isVofPartner: !!r.is_vof_partner,
  projectId: r.project_id,
  projectCode: r.project_code || '',
  projectName: r.project_name || '',
  contractingEntity: r.contracting_entity,
  stage: r.stage || '',
  hours: Number(r.hours) || 0,
  description: r.description || '',
  notes: r.notes || '',
  classification: r.classification,
  reason: r.reason,
  rate: r.rate == null ? null : Number(r.rate),
  currency: r.currency || null,
  amount: r.amount == null ? null : Number(r.amount),
  // Set when an admin moved this entry to SBPL from the statement page.
  overrideId: r.override_id == null ? null : Number(r.override_id),
  overrideReason: r.override_reason || '',
  overrideByName: r.override_by_name || '',
  overrideAt: r.override_at || null,
});

const byDateThenProject = (a, b) =>
  (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)
  || a.userName.localeCompare(b.userName)
  || a.projectCode.localeCompare(b.projectCode);

/**
 * Groups partner rows into everything the statement shows. Rows of people
 * who are not VOF partners are ignored.
 */
export const buildStatement = (rows) => {
  const partnerRows = (rows || []).filter(r => r.isVofPartner);

  const partners = new Map();
  const ensurePartner = (r) => {
    if (!partners.has(r.userId)) {
      const excluded = {};
      EXCLUDED_BUCKETS.forEach(k => { excluded[k] = 0; });
      partners.set(r.userId, {
        userId: r.userId,
        userName: r.userName,
        moduleA: { hours: 0, amount: 0 },
        moduleB: { hours: 0, amount: 0 },
        excluded,
        missingRate: 0,
      });
    }
    return partners.get(r.userId);
  };

  const projectsA = new Map();
  const detail = [];
  const excludedRows = [];
  const currencies = new Set();

  partnerRows.forEach(r => {
    const p = ensurePartner(r);
    if (r.classification === MODULE_A || r.classification === MODULE_B) {
      const bucket = r.classification === MODULE_A ? p.moduleA : p.moduleB;
      bucket.hours += r.hours;
      bucket.amount += r.amount || 0;
      if (r.amount == null) p.missingRate += 1;
      if (r.currency) currencies.add(r.currency);
      detail.push(r);
      if (r.classification === MODULE_A) {
        const key = `${r.userId}|${r.projectId}`;
        if (!projectsA.has(key)) {
          projectsA.set(key, {
            userId: r.userId, userName: r.userName,
            projectId: r.projectId, projectCode: r.projectCode, projectName: r.projectName,
            hours: 0, amount: 0,
          });
        }
        const pa = projectsA.get(key);
        pa.hours += r.hours;
        pa.amount += r.amount || 0;
      }
    } else {
      const bucket = excludedBucket(r);
      p.excluded[bucket] = (p.excluded[bucket] || 0) + r.hours;
      excludedRows.push({ ...r, bucket });
    }
  });

  const partnerList = [...partners.values()]
    .map(p => ({
      ...p,
      moduleA: { hours: round2(p.moduleA.hours), amount: round2(p.moduleA.amount) },
      moduleB: { hours: round2(p.moduleB.hours), amount: round2(p.moduleB.amount) },
      excluded: Object.fromEntries(Object.entries(p.excluded).map(([k, v]) => [k, round2(v)])),
      total: {
        hours: round2(p.moduleA.hours + p.moduleB.hours),
        amount: round2(p.moduleA.amount + p.moduleB.amount),
      },
    }))
    .sort((a, b) => a.userName.localeCompare(b.userName));

  const totals = partnerList.reduce((t, p) => ({
    moduleAHours: round2(t.moduleAHours + p.moduleA.hours),
    moduleBHours: round2(t.moduleBHours + p.moduleB.hours),
    moduleAAmount: round2(t.moduleAAmount + p.moduleA.amount),
    moduleBAmount: round2(t.moduleBAmount + p.moduleB.amount),
  }), { moduleAHours: 0, moduleBHours: 0, moduleAAmount: 0, moduleBAmount: 0 });
  totals.hours = round2(totals.moduleAHours + totals.moduleBHours);
  totals.amount = round2(totals.moduleAAmount + totals.moduleBAmount);

  return {
    partners: partnerList,
    projectsA: [...projectsA.values()]
      .map(p => ({ ...p, hours: round2(p.hours), amount: round2(p.amount) }))
      .sort((a, b) => a.userName.localeCompare(b.userName) || a.projectCode.localeCompare(b.projectCode)),
    detail: detail.sort(byDateThenProject),
    excludedRows: excludedRows.sort(byDateThenProject),
    totals,
    currency: currencies.size === 1 ? [...currencies][0] : (currencies.size ? 'mixed' : 'EUR'),
    missingRate: partnerList.reduce((n, p) => n + p.missingRate, 0),
  };
};

// ── CSV ─────────────────────────────────────────────────────────────────────

const csvCell = (v) => {
  if (v == null) return '';
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const DETAIL_COLUMNS = [
  ['Date', r => r.date],
  ['Partner', r => r.userName],
  ['Classification', r => r.classification],
  ['Project code', r => r.projectCode],
  ['Project', r => r.projectName],
  ['Stage', r => r.stage],
  ['Hours', r => r.hours],
  ['Rate', r => r.rate],
  ['Currency', r => r.currency],
  ['Amount', r => r.amount],
  ['Description', r => r.description],
  ['Notes', r => r.notes],
];

export const EXCLUDED_COLUMNS = [
  ['Date', r => r.date],
  ['Partner', r => r.userName],
  ['Reason', r => r.bucket],
  ['Moved because', r => r.overrideReason],
  ['Moved by', r => r.overrideByName],
  ['Project code', r => r.projectCode],
  ['Project', r => r.projectName],
  ['Stage', r => r.stage],
  ['Hours', r => r.hours],
  ['Description', r => r.description],
  ['Notes', r => r.notes],
];

/**
 * One CSV file with every section one after another, each under a title row,
 * so the whole statement travels as a single attachment.
 */
export const statementToCsv = (statement, period) => {
  const lines = [];
  const row = (cells) => lines.push(cells.map(csvCell).join(','));
  const cur = statement.currency;

  row(['VOF hours statement']);
  row(['Period', period.from, period.to]);
  row([]);

  row(['Summary per partner']);
  row(['Partner', 'Module A hours', `Module A amount (${cur})`, 'Module B hours', `Module B amount (${cur})`, 'Total hours', `Total amount (${cur})`, ...EXCLUDED_BUCKETS.map(b => `Excluded: ${b} (h)`)]);
  statement.partners.forEach(p => row([
    p.userName, p.moduleA.hours, p.moduleA.amount, p.moduleB.hours, p.moduleB.amount, p.total.hours, p.total.amount,
    ...EXCLUDED_BUCKETS.map(b => p.excluded[b] || 0),
  ]));
  row(['Total', statement.totals.moduleAHours, statement.totals.moduleAAmount, statement.totals.moduleBHours, statement.totals.moduleBAmount, statement.totals.hours, statement.totals.amount]);
  row([]);

  row(['Module A per project']);
  row(['Partner', 'Project code', 'Project', 'Hours', `Amount (${cur})`]);
  statement.projectsA.forEach(p => row([p.userName, p.projectCode, p.projectName, p.hours, p.amount]));
  row([]);

  row(['Detail, Module A and Module B']);
  row(DETAIL_COLUMNS.map(c => c[0]));
  statement.detail.forEach(r => row(DETAIL_COLUMNS.map(c => c[1](r))));
  row([]);

  row(['Excluded partner rows']);
  row(EXCLUDED_COLUMNS.map(c => c[0]));
  statement.excludedRows.forEach(r => row(EXCLUDED_COLUMNS.map(c => c[1](r))));

  return lines.join('\r\n');
};

export const statementFileName = (period, ext) =>
  `VOF_hours_statement_${period.from}_to_${period.to}.${ext}`;

// ── Table sorting ───────────────────────────────────────────────────────────

/**
 * Sorts rows by the accessor named `key`, in `direction` ('asc' | 'desc').
 * Numbers compare as numbers, everything else as text, ignoring case. Empty
 * values go last in either direction, and equal values keep their original
 * order, so a second click never shuffles ties. Returns a new array.
 */
export const sortRows = (rows, accessors, key, direction = 'asc') => {
  const get = accessors && accessors[key];
  if (!get) return [...(rows || [])];
  const sign = direction === 'desc' ? -1 : 1;
  return (rows || [])
    .map((row, i) => ({ row, i, v: get(row) }))
    .sort((a, b) => {
      const ae = a.v == null || a.v === '';
      const be = b.v == null || b.v === '';
      if (ae || be) return ae && be ? a.i - b.i : ae ? 1 : -1;
      const c = typeof a.v === 'number' && typeof b.v === 'number'
        ? a.v - b.v
        : String(a.v).localeCompare(String(b.v), undefined, { numeric: true, sensitivity: 'base' });
      return c !== 0 ? c * sign : a.i - b.i;
    })
    .map(x => x.row);
};

// ── Partner rate ranges ─────────────────────────────────────────────────────

/** The day before an ISO date. */
export const dayBefore = (iso) => {
  const { y, m0, d } = parts(iso);
  return isoDate(y, m0, d - 1);
};

/**
 * Adds `validTo` to each rate: the day before the same partner's next rate
 * starts, or null for the latest one (it runs on). Derived rather than stored,
 * so ranges can neither overlap nor leave a gap, and moving one rate's start
 * moves the end of the rate before it.
 */
export const withRateRanges = (rates) => {
  const byUser = new Map();
  (rates || []).forEach(r => {
    const k = String(r.userId);
    if (!byUser.has(k)) byUser.set(k, []);
    byUser.get(k).push(r);
  });
  const to = new Map();
  byUser.forEach(list => {
    const ordered = [...list].sort((a, b) => (a.validFrom < b.validFrom ? -1 : a.validFrom > b.validFrom ? 1 : 0));
    ordered.forEach((r, i) => to.set(r, ordered[i + 1] ? dayBefore(ordered[i + 1].validFrom) : null));
  });
  return (rates || []).map(r => ({ ...r, validTo: to.get(r) }));
};
